import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterShowAdUi } from 'library/ts/player/adapter/interfaces/events/detail/IPlayerAdapterShowAdUi';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { MouseButton } from 'library/ts/ui/enums/MouseButton';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { IAdUi } from 'tag/ts/tag/interfaces/ads/IAdUi';
import { IAdUiConfig } from 'tag/ts/tag/interfaces/ads/IAdUiConfig';
import { IAdClickOverlayManagerArgument } from 'tag/ts/tag/interfaces/arguments/IAdClickOverlayManagerArgument';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { ICustomAdUiElements } from 'tag/ts/tag/interfaces/tracking/ICustomAdUiElements';

/**
 * @copyright © Paramount Streaming
 * @author  Daniel Silva
 * @description Custom Ad UI: Ad Overlay Click Through Manager
 * @extends AbstractTagClass
 */
export class AdClickOverlayManager extends AbstractTagClass {
	#overlayEl: HTMLDivElement = null;
	#currentAdUi: IAdUi = null;
	#sdkTracking: ICustomAdUiElements;
	#getContainer: () => HTMLElement;

	constructor( argument: IAdClickOverlayManagerArgument ) {
		super( {
			...argument,
			name: 'AdClickOverlayManager'
		} );

		this.#getContainer = argument?.getContainer;
		this.#sdkTracking = this.tag?.model?.adUtilities?.customAdUiTracking;

		this.tagEventsHandler(
			this.#onTagEvent,
			[ TagEvent.ON_PLAYER_EVENT ]
		);
	}

	#onTagEvent = ( event: ITagEventVO ): void => {
		switch ( event?.type ) {
			case TagEvent.ON_PLAYER_EVENT:
				this.#onPlayerEvent( event?.data?.playerAdapterEvent );
				break;
		}
	};

	#onPlayerEvent = ( event: IPlayerAdapterEventVO ): void => {
		switch ( event?.type ) {
			case PlayerAdapterEvent.RESOURCE_END:
			// fall-through to overlay cleanup
			case PlayerAdapterEvent.HIDE_AD_UI:
			// fall-through to overlay cleanup
			case PlayerAdapterEvent.AD_BREAK_COMPLETE:
			// fall-through to overlay cleanup
			case PlayerAdapterEvent.AD_COMPLETE:
			// fall-through to overlay cleanup
			case PlayerAdapterEvent.CONTENT_SEGMENT_START:
				this.logger?.log( 'Ad Click Overlay cleanup:', event?.type );
				this.#destroyOverlay();
				this.#sdkTracking?.clear?.();
				this.#currentAdUi = null;
				break;

			case PlayerAdapterEvent.SHOW_AD_UI:
				// get adUi object
				const adUi = ( event?.detail as IPlayerAdapterShowAdUi )?.adUi;
				this.#currentAdUi = adUi ?? null;
				if ( this.utils.Type.isNull( adUi ) ) {
					this.logger?.log( 'No adUi, skipping overlay.' );
					return;
				}

				// check if ad needs click through functionality
				const config: IAdUiConfig = adUi.getConfig?.();
				const overlayConfig = config?.get?.( 'videoOverlay' );
				const isOverlayClickable = this.utils.Type.isObject( overlayConfig ) && !!overlayConfig?.clickable;
				this.logger?.log( 'SHOW_AD_UI', 'adUi:', this.#currentAdUi, 'config:', config, 'overlayConfig:', overlayConfig, 'isOverlayClickable:', isOverlayClickable );
				if ( !isOverlayClickable ) {
					this.logger?.log( 'videoOverlay not eligible, skipping overlay.' );
					return;
				}

				this.#createOverlay();

				// register element with SDK for tracking
				if ( this.utils.Type.isElement( this.#overlayEl ) && this.#sdkTracking ) {
					this.#sdkTracking.setElement( 'videoOverlay', this.#overlayEl );
					this.#sdkTracking.notifySDK( adUi );
				}
				break;
		}
	};

	/**
	 * @description Create the overlay element using injected container provider
	 */
	#createOverlay = (): void => {
		if ( this.#overlayEl ) {
			return; // overlay already exists
		}

		// resolve target container at creation time
		const container = this.#getContainer?.();
		if ( !this.utils.Type.isElement( container ) ) {
			this.logger?.log( 'Ad Click Overlay container not found or invalid.' );
			return;
		}

		const overlay = this.utils.DOM.stringToElement( (
			'<div class="ad-click-overlay" ' +
			'style="position:absolute; left:0; top:0; width:100%; height:100%; z-index: 99; cursor: pointer; background: transparent; touch-action: manipulation; user-select: none;"' +
			'></div>'
		) );

		// validate overlay element was created successfully
		if ( !this.utils.Type.isElement( overlay ) ) {
			this.logger?.warn( 'Ad Click Overlay element creation failed.' );
			return;
		}

		overlay.addEventListener( 'click', this.#onOverlayClick );
		container.appendChild( overlay );
		this.#overlayEl = overlay;

		this.logger?.log( 'Ad Click Overlay created' );
	};

	/**
	 * @description Destroy the overlay element
	 */
	#destroyOverlay = (): void => {
		if ( !this.#overlayEl ) {
			return; // overlay does not exist
		}

		this.#overlayEl?.removeEventListener( 'click', this.#onOverlayClick );
		if ( this.utils.Type.isElement( this.#overlayEl?.parentElement ) ) {
			this.#overlayEl.parentElement.removeChild( this.#overlayEl );
		}
		this.#overlayEl = null;
	};

	/**
	 * @description Handle overlay click events
	 * @param {Event} event
	 */
	#onOverlayClick = ( event: MouseEvent ): void => {
		if ( event?.type !== 'click' || event.button !== MouseButton.MAIN ) {
			event?.preventDefault?.(); // prevent right/middle click fallback behavior
			return;
		}

		// open clickthrough ad link
		if ( !this.utils.Type.isFunction( this.#currentAdUi?.onClick ) ) {
			this.logger?.log( 'Ad Click Overlay clicked: onClick() not available in AdUi object' );
			return;
		}
		this.#currentAdUi.onClick( 'videoOverlay', event );
		this.logger?.log( 'Ad Click Overlay clicked: calling SDK onClick(\'videoOverlay\')' );
	};
}